import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { UnitStatusBadge } from "@/components/StatusBadge";
import { Truck, Radio } from "lucide-react";

// Fix default icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const STATUS_COLOR = {
    disponible: "#10b981",
    ocupada: "#dc2626",
    mantenimiento: "#f59e0b",
    fuera_servicio: "#64748b",
};

function makeIcon(color) {
    return L.divIcon({
        className: "",
        html: `<div style="background:${color};width:28px;height:28px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:11px;font-family:IBM Plex Mono;">A</div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
    });
}

export default function LiveMap() {
    const [units, setUnits] = useState([]);
    const [services, setServices] = useState([]);

    const load = async () => {
        try {
            const [u, s] = await Promise.all([api.get("/units"), api.get("/dashboard/operational")]);
            if (u?.data) setUnits(u.data);
            if (s?.data) setServices(s.data.services || []);
        } catch (e) {
            console.warn("map fetch failed", e);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 20000);
        return () => clearInterval(t);
    }, []);

    const center = [19.4326, -99.1332]; // CDMX
    const counts = {
        disponible: units.filter((u) => u.status === "disponible").length,
        ocupada: units.filter((u) => u.status === "ocupada").length,
        mantenimiento: units.filter((u) => u.status === "mantenimiento").length,
        fuera_servicio: units.filter((u) => u.status === "fuera_servicio").length,
    };

    return (
        <div className="flex flex-col gap-3 h-[calc(100vh-180px)] min-h-[600px]" data-testid="live-map">
            {/* Stats bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                    { label: "Disponibles", value: counts.disponible, color: "bg-emerald-500" },
                    { label: "En Servicio", value: counts.ocupada, color: "bg-red-600" },
                    { label: "Mantenimiento", value: counts.mantenimiento, color: "bg-amber-500" },
                    { label: "Fuera Servicio", value: counts.fuera_servicio, color: "bg-slate-500" },
                ].map((s) => (
                    <div key={s.label} className="fm-card p-3 flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${s.color}`}></div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{s.label}</div>
                            <div className="font-mono font-bold text-xl tabular-nums">{s.value}</div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex-1 fm-card overflow-hidden relative min-h-[500px]">
                <MapContainer center={center} zoom={11} className="h-full w-full" data-testid="map-container">
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://osm.org">OpenStreetMap</a> contributors'
                    />
                    {units.filter((u) => u.lat && u.lng).map((u) => (
                        <Marker
                            key={u.id}
                            position={[u.lat, u.lng]}
                            icon={makeIcon(STATUS_COLOR[u.status] || "#64748b")}
                        >
                            <Popup>
                                <div className="font-sans">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Truck className="w-3.5 h-3.5" strokeWidth={1.5} />
                                        <span className="font-mono font-bold">{u.economic_number}</span>
                                    </div>
                                    <div className="text-xs">{u.brand} {u.model} · {u.year}</div>
                                    <div className="text-xs text-slate-500">{u.ambulance_type}</div>
                                    <div className="text-xs text-slate-500">{u.base}</div>
                                    <div className="mt-1.5">
                                        <UnitStatusBadge status={u.status} />
                                    </div>
                                </div>
                            </Popup>
                        </Marker>
                    ))}

                    {services.filter((s) => s.origin_lat && s.origin_lng).map((s) => (
                        <CircleMarker
                            key={`s-${s.id}`}
                            center={[s.origin_lat, s.origin_lng]}
                            radius={6}
                            fillColor={s.urgency_level === "critica" ? "#ef4444" : s.urgency_level === "alta" ? "#f59e0b" : "#0f172a"}
                            color="white"
                            weight={2}
                            fillOpacity={0.9}
                        >
                            <Popup>
                                <div className="font-sans">
                                    <div className="font-mono font-bold text-xs">{s.folio}</div>
                                    <div className="text-xs mt-1">{s.patient_name}</div>
                                    <div className="text-[10px] text-slate-500">{s.service_type_name}</div>
                                </div>
                            </Popup>
                        </CircleMarker>
                    ))}
                </MapContainer>

                <div className="absolute top-3 right-3 fm-card p-3 z-[1000] space-y-1.5 text-xs">
                    <div className="font-display font-bold text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
                        <Radio className="w-3 h-3" /> Leyenda
                    </div>
                    {[
                        ["bg-emerald-500", "Disponible"],
                        ["bg-red-600", "Ocupada"],
                        ["bg-amber-500", "Mantenimiento"],
                        ["bg-slate-500", "Fuera servicio"],
                    ].map(([cls, lbl]) => (
                        <div key={lbl} className="flex items-center gap-2 text-[11px]">
                            <span className={`w-2.5 h-2.5 rounded-full ${cls}`}></span>
                            <span className="text-slate-700">{lbl}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
